import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Container, Input, InputGroup } from "reactstrap";

import useAgentStatus from "Hooks/useAgentStatus";
import IntegrationRow from "Pages/Integration/Components/IntegrationRow/IntegrationRow";
import RunToasts, {
    IRunToast,
} from "Pages/Integration/Components/RunToasts/RunToasts";
import styles from "Pages/Integration/Integrations.module.css";
import useFetch from "Hooks/useFetch";
import useUserContext from "Hooks/useUserContext";
import usePageTitle from "Hooks/usePageTitle";
import useAlertContext from "Hooks/useAlertContext";
import { applyIntegrationSystemOverrides } from "Pages/Integration/systemDirectionOverrides";

const PREF_USE_NEW_LAUNCH_PAGE = "use_new_launch_page";
const PREF_LAUNCH_FAVORITES = "launch_favorites";
const PREF_LAUNCH_TILE_ORDER = "launch_tile_order";
const UNGROUPED_LABEL = "Ungrouped";

function parseFavoriteProcesses(raw: string | undefined): string[] {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter((value): value is string => typeof value === "string")
            .map((value) => value.trim())
            .filter(Boolean);
    } catch {
        return [];
    }
}

function parseStringArray(raw: string | undefined): string[] {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter((value): value is string => typeof value === "string")
            .map((value) => value.trim())
            .filter(Boolean);
    } catch {
        return [];
    }
}

function normalizeStringArray(raw: unknown): string[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((value): value is string => typeof value === "string")
        .map((value) => value.trim())
        .filter(Boolean);
}

function applySavedTileOrder(
    groupNames: string[],
    savedOrder: string[]
): string[] {
    const available = new Set(groupNames);
    const next: string[] = [];

    for (const name of savedOrder) {
        if (available.has(name) && !next.includes(name)) {
            next.push(name);
        }
    }

    for (const name of groupNames) {
        if (!next.includes(name)) {
            next.push(name);
        }
    }

    return next;
}

function reorderGroupNames(
    groupNames: string[],
    sourceGroup: string,
    targetGroup: string
): string[] {
    const sourceIndex = groupNames.indexOf(sourceGroup);
    const targetIndex = groupNames.indexOf(targetGroup);
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
        return groupNames;
    }

    const next = [...groupNames];
    const [moved] = next.splice(sourceIndex, 1);

    // Insert at the hovered tile's slot in the post-removal list so forward
    // moves do not land one position too early.
    next.splice(targetIndex, 0, moved);
    return next;
}

export default function Integrations() {
    const [integrations, setIntegrations] = useState<IIntegration[]>([]);

    const lastFilter = localStorage.getItem("integrationHomeFilter");
    const [searchTerm, setSearchTerm] = useState<string>(lastFilter || "");

    const [userContext, onSetUserContext] = useUserContext();
    const { newAlert } = useAlertContext();
    const [pendingFavoriteSaves, setPendingFavoriteSaves] = useState<Set<string>>(
        new Set()
    );
    const [tileOrder, setTileOrder] = useState<string[]>([]);
    const [draggingGroup, setDraggingGroup] = useState<string | null>(null);
    const [dragOverGroup, setDragOverGroup] = useState<string | null>(null);

    /** Persistent, stacking toasts for inline job runs (run_inline preference).
     * Separate from the shared alert system so multiple contract runs each get
     * their own toast that stays up until dismissed. */
    const [runToasts, setRunToasts] = useState<IRunToast[]>([]);
    const runToastIdRef = useRef(0);
    const isMountedRef = useRef(false);

    const pushRunToast = useCallback((message: string) => {
        const id = ++runToastIdRef.current;
        setRunToasts((prev) => [...prev, { id, message }]);
    }, []);

    const dismissRunToast = useCallback((id: number) => {
        setRunToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    usePageTitle("Launch");
    
    useAgentStatus();
    const { get, post, put } = useFetch();

    const fetchIntegrations = useCallback(async () => {
        const response = await get("/api/integrations");
        const data = await response.json();
        console.log("Get integrations response: ", data);

        if (response.ok && isMountedRef.current) {
            setIntegrations(
                applyIntegrationSystemOverrides(data, userContext.companyId)
            );
        }
    }, [get, userContext.companyId]);

    // Get integrations and arguments
    useEffect(() => {
        isMountedRef.current = true;
        fetchIntegrations();

        return () => {
            isMountedRef.current = false;
        };
    }, [fetchIntegrations]);

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        setSearchTerm(e.target.value);
        localStorage.setItem("integrationHomeFilter", e.target.value);
    }

    /** Clear search filter with button click */
    function resetFilter(e: React.MouseEvent) {
        e.preventDefault();
        setSearchTerm("");
        localStorage.setItem("integrationHomeFilter", "");
    }

    /** Clear search filter with esc key */
    function clearWithEscape(e: React.KeyboardEvent) {
        if (e.key === "Escape") {
            e.preventDefault();
            setSearchTerm("");
            localStorage.setItem("integrationHomeFilter", "");
        }
    }

    const integrationsShown = integrations.filter(
        (i) =>
            !i.hidden && i.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const useNewLaunchPage =
        userContext.preferences?.[PREF_USE_NEW_LAUNCH_PAGE] === "true";

    const favoriteProcesses = useMemo(
        () => parseFavoriteProcesses(userContext.preferences?.[PREF_LAUNCH_FAVORITES]),
        [userContext.preferences]
    );

    const favoriteProcessSet = useMemo(
        () => new Set(favoriteProcesses),
        [favoriteProcesses]
    );

    const groupedIntegrations = useMemo(() => {
        const grouped = new Map<string, IIntegration[]>();
        for (const integration of integrationsShown) {
            const groupName = integration.integration_group?.trim() || UNGROUPED_LABEL;
            const rows = grouped.get(groupName) ?? [];
            rows.push(integration);
            grouped.set(groupName, rows);
        }
        return grouped;
    }, [integrationsShown]);

    const groupedNames = useMemo(
        () => Array.from(groupedIntegrations.keys()),
        [groupedIntegrations]
    );

    const savedTileOrder = useMemo(
        () => parseStringArray(userContext.preferences?.[PREF_LAUNCH_TILE_ORDER]),
        [userContext.preferences]
    );

    useEffect(() => {
        setTileOrder((previous) => {
            const basis = savedTileOrder.length > 0 ? savedTileOrder : previous;
            const next = applySavedTileOrder(groupedNames, basis);
            if (
                previous.length === next.length &&
                previous.every((name, index) => name === next[index])
            ) {
                return previous;
            }
            return next;
        });
    }, [groupedNames, savedTileOrder]);

    const orderedGroupedIntegrations = useMemo(
        () =>
            tileOrder
                .map((groupName) => {
                    const rows = groupedIntegrations.get(groupName);
                    return rows ? ([groupName, rows] as const) : null;
                })
                .filter(
                    (
                        entry
                    ): entry is readonly [
                        string,
                        IIntegration[]
                    ] => entry !== null
                ),
        [tileOrder, groupedIntegrations]
    );

    const favoriteIntegrations = useMemo(
        () =>
            integrationsShown.filter((integration) =>
                favoriteProcessSet.has(integration.process)
            ),
        [integrationsShown, favoriteProcessSet]
    );

    const integrationIndexById = useMemo(() => {
        const lookup = new Map<number, number>();
        integrationsShown.forEach((integration, idx) => {
            lookup.set(integration.id, idx);
        });
        return lookup;
    }, [integrationsShown]);

    const onToggleFavorite = useCallback(
        async (row: IIntegration) => {
            if (pendingFavoriteSaves.size > 0) return;

            const nextSet = new Set(
                parseFavoriteProcesses(userContext.preferences?.[PREF_LAUNCH_FAVORITES])
            );
            if (nextSet.has(row.process)) nextSet.delete(row.process);
            else nextSet.add(row.process);

            const nextValue = JSON.stringify(Array.from(nextSet));

            setPendingFavoriteSaves((prev) => {
                const next = new Set(prev);
                next.add(row.process);
                return next;
            });

            const response = await put(`/api/me/preferences/${PREF_LAUNCH_FAVORITES}`, {
                value: nextValue,
            });

            setPendingFavoriteSaves((prev) => {
                const next = new Set(prev);
                next.delete(row.process);
                return next;
            });

            if (response.ok) {
                onSetUserContext({
                    preferences: {
                        ...userContext.preferences,
                        [PREF_LAUNCH_FAVORITES]: nextValue,
                    },
                });
                return;
            }

            newAlert({
                color: "danger",
                message: "Failed to save favorites preference",
            });
        },
        [
            favoriteProcesses,
            pendingFavoriteSaves,
            put,
            onSetUserContext,
            userContext.preferences,
            newAlert,
        ]
    );

    const saveTileOrderPreference = useCallback(
        async (nextOrder: string[]) => {
            const value = JSON.stringify(nextOrder);
            const response = await put(`/api/me/preferences/${PREF_LAUNCH_TILE_ORDER}`, {
                value,
            });

            if (!response.ok) {
                newAlert({
                    color: "danger",
                    message: "Failed to save launch tile order",
                });
                return false;
            }

            onSetUserContext({
                preferences: {
                    ...userContext.preferences,
                    [PREF_LAUNCH_TILE_ORDER]: value,
                },
            });
            return true;
        },
        [put, newAlert, onSetUserContext, userContext.preferences]
    );

    const handleTileDragStart = useCallback(
        (
            event: React.DragEvent<HTMLElement>,
            groupName: string
        ) => {
            setDraggingGroup(groupName);
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", groupName);
        },
        []
    );

    const handleTileDragOver = useCallback(
        (event: React.DragEvent<HTMLElement>, groupName: string) => {
            if (!draggingGroup || draggingGroup === groupName) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            if (dragOverGroup !== groupName) {
                setDragOverGroup(groupName);
            }
        },
        [draggingGroup, dragOverGroup]
    );

    const handleTileDrop = useCallback(
        async (event: React.DragEvent<HTMLElement>, targetGroup: string) => {
            event.preventDefault();
            const sourceGroup =
                draggingGroup || event.dataTransfer.getData("text/plain");
            setDragOverGroup(null);
            setDraggingGroup(null);

            if (!sourceGroup || sourceGroup === targetGroup) return;

            const previousOrder = tileOrder;
            const nextOrder = reorderGroupNames(
                previousOrder,
                sourceGroup,
                targetGroup
            );
            if (nextOrder === previousOrder) return;

            setTileOrder(nextOrder);
            const saved = await saveTileOrderPreference(nextOrder);
            if (!saved) {
                setTileOrder(previousOrder);
            }
        },
        [draggingGroup, tileOrder, saveTileOrderPreference]
    );

    const handleTileDragEnd = useCallback(() => {
        setDragOverGroup(null);
        setDraggingGroup(null);
    }, []);

    const canResetToProviderDefaults = userContext.isCalanceAdmin === "1";

    const resetToProviderDefaults = useCallback(async () => {
        const confirmed = window.confirm(
            "Reset integrations to provider defaults? This will replace the current integration list for this company."
        );
        if (!confirmed) return;

        const response = await post("/api/integrations/reset_defaults");
        let data: any = {};
        try {
            data = await response.json();
        } catch {
            data = {};
        }

        if (response.ok) {
            const launchDefaults = data.launch_defaults ?? {};
            const launchTileOrder = normalizeStringArray(launchDefaults.tile_order);
            const launchFavorites = normalizeStringArray(
                launchDefaults.favorite_processes
            );

            if (launchTileOrder.length > 0 || launchFavorites.length > 0) {
                const nextPreferences = { ...userContext.preferences };
                if (launchTileOrder.length > 0) {
                    nextPreferences[PREF_LAUNCH_TILE_ORDER] = JSON.stringify(
                        launchTileOrder
                    );
                    setTileOrder(launchTileOrder);
                }
                if (launchFavorites.length > 0) {
                    nextPreferences[PREF_LAUNCH_FAVORITES] = JSON.stringify(
                        launchFavorites
                    );
                }

                onSetUserContext({ preferences: nextPreferences });
            }

            await fetchIntegrations();
            newAlert({
                color: "success",
                message: `Reset integrations to provider defaults (${data.integration_count ?? 0} loaded).`,
            });
            return;
        }

        newAlert({
            color: "danger",
            message: data.detail ?? "Failed to reset integrations to provider defaults.",
        });
    }, [
        post,
        fetchIntegrations,
        newAlert,
        onSetUserContext,
        userContext.preferences,
    ]);

    return (
        <Container className={styles.container}>
            <h2 className={styles.header}>
                {userContext.companyName + " Integrations"}
            </h2>

            {/* Search input */}
            <InputGroup className={styles.searchInput}>
                <Input
                    autoFocus
                    type="text"
                    style={
                        searchTerm
                            ? {
                                  backgroundColor:
                                      "var(--dimensionBlueHighlight)",
                              }
                            : {}
                    }
                    value={searchTerm}
                    onChange={handleInputChange}
                    onKeyDown={clearWithEscape}
                    placeholder="Filter integrations..."
                />
                <Button className={styles.resetBtn} onClick={resetFilter}>
                    ╳
                </Button>
            </InputGroup>

            {/* Integrations */}
            {!useNewLaunchPage &&
                integrationsShown.map(
                    (i, index, array) =>
                        !i.hidden && (
                            <IntegrationRow
                                key={i.name + index}
                                row={i}
                                index={index}
                                array={array}
                                pushRunToast={pushRunToast}
                            />
                        )
                )}

            {useNewLaunchPage && (
                <>
                    <div className={styles.tilesGrid}>
                        {favoriteIntegrations.length > 0 && (
                            <section className={styles.tile}>
                                <h3 className={styles.tileTitle}>Favorites</h3>
                                <div className={styles.tileBody}>
                                    {favoriteIntegrations.map((integration) => (
                                        <IntegrationRow
                                            key={`favorite-${integration.id}`}
                                            instanceKey={`favorite-${integration.id}`}
                                            row={integration}
                                            index={integrationIndexById.get(integration.id) ?? 0}
                                            array={favoriteIntegrations}
                                            variant="tile"
                                            showGroupHeader={false}
                                            isPinned={true}
                                            onToggleFavorite={onToggleFavorite}
                                            isPinSaving={pendingFavoriteSaves.has(
                                                integration.process
                                            )}
                                            pushRunToast={pushRunToast}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}

                        {orderedGroupedIntegrations.map(([groupName, rows]) => (
                            <section
                                className={`${styles.tile} ${
                                    dragOverGroup === groupName ? styles.dragOverTile : ""
                                } ${draggingGroup === groupName ? styles.draggingTile : ""}`}
                                key={groupName}
                                onDragOver={(event) =>
                                    handleTileDragOver(event, groupName)
                                }
                                onDrop={(event) => handleTileDrop(event, groupName)}
                                onDragEnd={handleTileDragEnd}
                            >
                                <h3
                                    className={`${styles.tileTitle} ${styles.tileDragHandle}`}
                                    draggable
                                    onDragStart={(event) =>
                                        handleTileDragStart(event, groupName)
                                    }
                                >
                                    {groupName}
                                </h3>
                                <div className={styles.tileBody}>
                                    {rows.map((integration) => (
                                        <IntegrationRow
                                            key={`group-${groupName}-${integration.id}`}
                                            instanceKey={`group-${groupName}-${integration.id}`}
                                            row={integration}
                                            index={integrationIndexById.get(integration.id) ?? 0}
                                            array={rows}
                                            variant="tile"
                                            showGroupHeader={false}
                                            isPinned={favoriteProcessSet.has(
                                                integration.process
                                            )}
                                            onToggleFavorite={onToggleFavorite}
                                            isPinSaving={pendingFavoriteSaves.has(
                                                integration.process
                                            )}
                                            pushRunToast={pushRunToast}
                                        />
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>

                    {canResetToProviderDefaults && (
                        <div className={styles.resetDefaultsBox}>
                            <Button
                                color="primary"
                                onClick={resetToProviderDefaults}
                            >
                                Reset Integrations To Provider Defaults
                            </Button>
                        </div>
                    )}
                </>
            )}

            <RunToasts toasts={runToasts} onDismiss={dismissRunToast} />
        </Container>
    );
}
